import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnDestroy, output, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { DeviceSelectorComponent } from '../../device-selector.component';
import { SelectComponent, SelectOption } from '../../ui/select/select.component';
import { WaitingRoomListComponent } from '../waiting-room-list/waiting-room-list.component';
import { ChimeService } from '../chime.service';

@Component({
  selector: 'app-join-call-settings',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatIconModule,
    MatCheckboxModule,
    DeviceSelectorComponent,
    SelectComponent,
    MatTooltipModule,
    WaitingRoomListComponent,
  ],
  template: `
    <div class="join-wrapper" [class.is-drawer]="isDrawer()">
      <div class="video-preview-container" [class.is-speaking]="isSpeaking()">
        @if (chimeService.isCameraEnabled() && cameraPermissionState() === 'granted' && (!chimeService.isCameraLocked() || isContentEditor())) {
          <video #videoPreview class="video-preview" autoplay muted></video>
        } @else {
          <div class="no-video-placeholder">
            <mat-icon>videocam_off</mat-icon>
          </div>
        }
      </div>
      <div class="form-container d-flex flex-column gap-2">
        <mat-form-field appearance="fill" class="mb-2">
          <input matInput [ngModel]="username()" (ngModelChange)="usernameChange.emit($event)" [placeholder]="'Streaming.RoomEntrance.YourName' | translate" required />
        </mat-form-field>

        @if (isGuest()) {
          <mat-form-field appearance="fill" class="mb-2">
            <input matInput [ngModel]="email()" (ngModelChange)="emailChange.emit($event)" [placeholder]="'Streaming.RoomEntrance.YourEmail' | translate" type="email" required />
          </mat-form-field>
        }

        @if (isHost() && waitingParticipants().length > 0) {
          <app-waiting-room-list [participants]="waitingParticipants()" [collapsible]="true" (admit)="admitParticipant.emit($event)" (reject)="rejectParticipant.emit($event)" />
        }

        @if (isDrawer() && isContentEditor()) {
          <div class="meeting-lobby-settings">
            <button class="settings-section-header" [class.expanded]="!isSettingsCollapsed()" (click)="isSettingsCollapsed.set(!isSettingsCollapsed())">
              <span class="text-[12px] font-semibold tracking-wider">{{ 'VideoCall.Lobby.SettingsTitle' | translate }}</span>
              <mat-icon class="toggle-icon" [class.expanded]="!isSettingsCollapsed()">chevron_right</mat-icon>
            </button>
            @if (!isSettingsCollapsed()) {
              <div class="settings-body d-flex flex-column gap-2">
                <mat-slide-toggle dense [checked]="chimeService.isAudioLocked()" (change)="onLockAudioToggle($event)" color="primary" class="d-block text-xs">
                  {{ 'VideoCall.Lobby.LockAudio' | translate }}
                </mat-slide-toggle>
                <mat-slide-toggle dense [checked]="chimeService.isCameraLocked()" (change)="onLockCameraToggle($event)" color="primary" class="d-block text-xs">
                  {{ 'VideoCall.Lobby.LockCamera' | translate }}
                </mat-slide-toggle>
                <mat-slide-toggle dense [checked]="chimeService.isScreenShareLocked()" (change)="onLockScreenShareToggle($event)" color="primary" class="d-block text-xs">
                  {{ 'VideoCall.Lobby.LockScreenShare' | translate }}
                </mat-slide-toggle>
                <mat-slide-toggle dense [checked]="chimeService.isChatLocked()" (change)="onLockChatToggle($event)" color="primary" class="d-block text-xs">
                  {{ 'VideoCall.Lobby.LockChat' | translate }}
                </mat-slide-toggle>
                <mat-slide-toggle dense [checked]="chimeService.isWaitingRoomEnabled()" (change)="onWaitingRoomToggle($event)" color="primary" class="d-block text-xs">
                  {{ 'VideoCall.Lobby.WaitingRoom' | translate }}
                </mat-slide-toggle>
              </div>
            }
          </div>
        }

        <div id="media-toggles" class="d-flex flex-column mb-1">
          @if (!chimeService.isCameraLocked() || isContentEditor()) {
            <mat-slide-toggle class="d-block mb-1" [checked]="chimeService.isCameraEnabled()" (change)="onCameraToggleChange($event)" color="primary">
              {{ 'Streaming.RoomEntrance.Camera' | translate }}
            </mat-slide-toggle>
          }
          @if (!chimeService.isAudioLocked() || isContentEditor()) {
            <mat-slide-toggle class="d-block" [checked]="!chimeService.isMuted()" (change)="onMicrophoneToggleChange($event)" color="primary">
              {{ 'Streaming.RoomEntrance.Microphone' | translate }}
            </mat-slide-toggle>
          }
        </div>

        @if (!chimeService.isCameraLocked() || isContentEditor()) {
          <mat-checkbox [checked]="chimeService.isBlurEnabled()" (change)="onBlurOnJoinChange($event)" [disabled]="!chimeService.isCameraEnabled()" color="primary">
            {{ 'VideoCall.ShouldBlurOnJoin' | translate }}
          </mat-checkbox>
        }

        @if (chimeService.isCameraEnabled() && (!chimeService.isCameraLocked() || isContentEditor()) && chimeService.isBlurEnabled()) {
          <div class="w-full text-left mt-1">
            <label class="form-label mb-1 block text-xs font-semibold text-slate-600">{{ 'VideoCall.BlurStrength.Label' | translate }}</label>
            <ui-select
              class="w-full"
              [options]="blurStrengthOptions()"
              [ngModel]="chimeService.blurStrength()"
              (valueChange)="onBlurStrengthChange($any($event))"
              [compact]="true"
            ></ui-select>
          </div>
        }

        <div class="device-selectors d-flex flex-column gap-2 mt-2">
          @if (chimeService.isCameraEnabled() && cameraPermissionState() === 'granted' && (!chimeService.isCameraLocked() || isContentEditor())) {
            <app-device-selector [kind]="'videoinput'" [label]="'DeviceSelector.CameraLabel'" (selectionChange)="onCameraSelect($event)" />
          }
          @if (!chimeService.isMuted() && microphonePermissionState() === 'granted' && (!chimeService.isAudioLocked() || isContentEditor())) {
            <app-device-selector [kind]="'audioinput'" [label]="'DeviceSelector.MicrophoneLabel'" (selectionChange)="onMicrophoneSelect($event)" />
          }
        </div>

        <button mat-flat-button class="mt-2 full-width" color="primary" [disabled]="loading() || !username().trim() || (isGuest() && !email().trim())" (click)="joinCall.emit()">
          @if (loading()) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <span>{{ 'MediaRecorder.JoinCall' | translate }}</span>
          }
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./join-call-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinCallSettingsComponent implements AfterViewInit, OnDestroy {
  loading = input(false);
  username = input('');
  usernameChange = output<string>();
  isGuest = input(false);
  email = input('');
  emailChange = output<string>();
  joinCall = output<void>();
  callID = input.required<string>();
  isDrawer = input(false);
  isSettingsCollapsed = signal(true);
  isHost = input(false);
  waitingParticipants = input<any[]>([]);
  admitParticipant = output<string>();
  rejectParticipant = output<string>();

  public chimeService = inject(ChimeService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);
  private translate = inject(TranslateService);
  public authService = inject(AuthService);
  private contentStateService = inject(ContentStateService);

  isContentEditor = this.contentStateService.isContentEditor;

  cameraPermissionState = toSignal(this.permissionWatcher.cameraPermission$, { initialValue: 'prompt' });
  microphonePermissionState = toSignal(this.permissionWatcher.microphonePermission$, { initialValue: 'prompt' });

  blurStrengthOptions = computed<SelectOption[]>(() => [
    { value: 'low', label: this.translate.instant('VideoCall.BlurStrength.Low') },
    { value: 'medium', label: this.translate.instant('VideoCall.BlurStrength.Medium') },
    { value: 'high', label: this.translate.instant('VideoCall.BlurStrength.High') },
  ]);

  onBlurStrengthChange(strength: 'low' | 'medium' | 'high'): void {
    this.chimeService.setBlurStrength(strength);
  }

  onLockAudioToggle(event: MatSlideToggleChange) {
    this.chimeService.updateMeetingSettings(this.callID(), { isAudioLocked: event.checked });
  }

  onLockCameraToggle(event: MatSlideToggleChange) {
    this.chimeService.updateMeetingSettings(this.callID(), { isCameraLocked: event.checked });
  }

  onLockScreenShareToggle(event: MatSlideToggleChange) {
    this.chimeService.updateMeetingSettings(this.callID(), { isScreenShareLocked: event.checked });
  }

  onLockChatToggle(event: MatSlideToggleChange) {
    this.chimeService.updateMeetingSettings(this.callID(), { isChatLocked: event.checked });
  }

  onWaitingRoomToggle(event: MatSlideToggleChange) {
    this.chimeService.updateMeetingSettings(this.callID(), { isWaitingRoomEnabled: event.checked });
  }

  onResetSettings() {
    this.chimeService.updateMeetingSettings(this.callID(), {
      isAudioLocked: false,
      isCameraLocked: false,
      isScreenShareLocked: false,
      isChatLocked: false,
      isWaitingRoomEnabled: false,
    });
  }

  // Audio analysis variables
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private dataArray: Uint8Array | null = null;
  private animationFrameId: number | null = null;
  private micStream: MediaStream | null = null;
  private audioRequestId = 0;

  volumeLevel = signal(0);
  isSpeaking = computed(() => this.volumeLevel() > 0.05);

  @ViewChild('videoPreview') videoPreview: ElementRef<HTMLVideoElement> | undefined;

  async ngAfterViewInit(): Promise<void> {
    if (this.chimeService.isCameraEnabled() && (!this.chimeService.isCameraLocked() || this.isContentEditor())) {
      const camAccess = await firstValueFrom(this.permissionWatcher.cameraPermission$);
      if (camAccess === 'denied') {
        this.chimeService.setCameraEnabled(false);
      } else {
        this.updatePreview();
      }
    }
    if (!this.chimeService.isMuted() && (!this.chimeService.isAudioLocked() || this.isContentEditor())) {
      const micAccess = await firstValueFrom(this.permissionWatcher.microphonePermission$);
      if (micAccess === 'denied') {
        this.chimeService.setMicrophoneEnabled(false);
      } else {
        this.startAudioAnalysis();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopAudioAnalysis();
    if (this.chimeService.videoPreviewIsActive()) {
      this.chimeService.stopVideoPreview();
    }
  }

  async onCameraToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      let hasAccess = this.cameraPermissionState() === 'granted';
      if (!hasAccess) {
        hasAccess = await this.permissionWatcher.requestCameraAccess();
      }
      if (hasAccess) {
        this.chimeService.setCameraEnabled(true);
        this.updatePreview();
      } else {
        this.chimeService.setCameraEnabled(false);
      }
    } else {
      this.chimeService.setCameraEnabled(false);
      await this.chimeService.stopVideoPreview();
    }
  }

  async onMicrophoneToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      let hasAccess = this.microphonePermissionState() === 'granted';
      if (!hasAccess) {
        hasAccess = await this.permissionWatcher.requestMicrophoneAccess();
      }
      if (hasAccess) {
        this.chimeService.setMicrophoneEnabled(true);
        this.startAudioAnalysis();
      } else {
        this.chimeService.setMicrophoneEnabled(false);
      }
    } else {
      this.chimeService.setMicrophoneEnabled(false);
      this.stopAudioAnalysis();
    }
  }

  onBlurOnJoinChange(event: MatCheckboxChange) {
    this.chimeService.setBlurPreference(event.checked);
  }

  onCameraSelect(deviceId: string): void {
    this.chimeService.selectVideoInput(deviceId);
    this.updatePreview();
  }

  onMicrophoneSelect(deviceId: string): void {
    this.chimeService.selectAudioInput(deviceId);
    this.startAudioAnalysis();
  }

  private async startAudioAnalysis(): Promise<void> {
    const requestId = ++this.audioRequestId;
    this.stopAudioAnalysis();

    if (this.chimeService.isMuted() || (this.chimeService.isAudioLocked() && !this.isContentEditor())) return;

    try {
      const savedMic = localStorage.getItem('selectedMicrophone');
      let constraints: any = { audio: true };
      if (savedMic && savedMic !== 'default') {
        constraints.audio = { deviceId: { exact: savedMic } };
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        console.warn('[JoinCallSettings] Failed exact microphone, falling back to ideal/default:', err);
        constraints = { audio: true };
        if (savedMic && savedMic !== 'default') {
          constraints.audio = { deviceId: { ideal: savedMic } };
        }
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      if (requestId !== this.audioRequestId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.micStream = stream;

      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

      this.source = this.audioContext.createMediaStreamSource(this.micStream);
      this.source.connect(this.analyser);

      this.analyzeAudio();
    } catch (err) {
      console.error('Failed to start audio analysis for green room preview', err);
    }
  }

  private analyzeAudio = () => {
    if (!this.analyser || !this.dataArray) return;
    this.animationFrameId = requestAnimationFrame(this.analyzeAudio);

    this.analyser.getByteFrequencyData(this.dataArray as Uint8Array<ArrayBuffer>);

    let sum = 0;
    for (const amplitude of this.dataArray) {
      sum += amplitude * amplitude;
    }
    const average = Math.sqrt(sum / this.dataArray.length);
    const normalizedVolume = Math.min(1, average / 128); // normalize between 0 and 1

    this.volumeLevel.set(normalizedVolume);
  };

  private stopAudioAnalysis(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(console.error);
    }
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;

    if (this.micStream) {
      this.micStream.getTracks().forEach((track) => track.stop());
      this.micStream = null;
    }
    this.volumeLevel.set(0);
  }

  private updatePreview(): void {
    // Need to wait for the element to be in the DOM
    setTimeout(async () => {
      if (this.videoPreview) {
        await this.chimeService.startVideoPreview(this.videoPreview.nativeElement);
      }
    }, 0);
  }
}
